import type { ReactNode } from "react";
import type { PreviewDevice } from "@/modules/manage/components/StorefrontPreview";

/**
 * The frame the preview sits in, from `Trioty Restaurant Editor.dc.html`.
 *
 * Desktop is a browser window — traffic lights and the store's own address —
 * and phone is a handset with a notch. It is not decoration: the two frames are
 * what tell a seller *which* page they are looking at, and a restaurant theme
 * is two designs, not one responsive design. Without the frame a 390px column
 * floating in grey is ambiguous.
 *
 * The frames carry the ACTIVE THEME's page colour behind the preview so the
 * moment before the iframe paints is the right colour rather than white.
 */
export function DeviceFrame({
  device,
  urlLabel,
  pageColor,
  chromeBg,
  chromeFg,
  children,
}: {
  device: PreviewDevice;
  /** `khalifa-restaurant.trioty.shop` — what the seller would type. */
  urlLabel: string;
  pageColor: string;
  chromeBg: string;
  chromeFg: string;
  children: ReactNode;
}) {
  if (device === "phone") {
    return (
      <div
        style={{
          width: 330,
          height: "100%",
          maxHeight: 680,
          borderRadius: 34,
          border: "8px solid #17171A",
          overflow: "hidden",
          boxShadow: "0 22px 54px rgba(0,0,0,.3)",
          background: pageColor,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            flexShrink: 0,
            height: 22,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-end",
            background: pageColor,
          }}
        >
          <span
            style={{
              width: 74,
              height: 16,
              borderRadius: 99,
              background: "#17171A",
            }}
          />
        </div>
        <div style={{ flex: 1, minHeight: 0 }}>{children}</div>
      </div>
    );
  }

  return (
    <div
      style={{
        width: "min(880px, 100%)",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 12,
        overflow: "hidden",
        boxShadow: "0 18px 50px rgba(0,0,0,.18)",
        background: pageColor,
      }}
    >
      <div
        style={{
          flexShrink: 0,
          height: 26,
          background: chromeBg,
          display: "flex",
          alignItems: "center",
          gap: 5,
          padding: "0 12px",
        }}
      >
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#FF5F57" }} />
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#FEBC2E" }} />
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#28C840" }} />
        <span
          style={{
            flex: 1,
            textAlign: "center",
            fontSize: 9,
            fontWeight: 600,
            color: chromeFg,
            /* An address reads left-to-right in both locales. */
            direction: "ltr",
          }}
        >
          {urlLabel}
        </span>
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>{children}</div>
    </div>
  );
}
